import * as React from 'react';
import { formatDateTime } from "../../misc/components/Formatters/Formatters";
import { SessionMetadata, AnalysisSet } from '../../misc/api/apiTypes';
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { getSessionMetadataTableAutomationId, SessionMetadataTableAutomationId } from '../../misc/Helpers/AutomationIdHelper';

export interface SessionMetadataTableProps {
    sessionMetadata: SessionMetadata;
    selectedAnalysisSetId: string;
    isQc?: boolean;
}

type AnalysisSetStatusAnalyst = { Id: string, Status: { Name: string }, Analyst?: { Username: string }, Type?: { Id?: string } };

export interface LeanSessionMetadata {
    Id: string;
    SiteId: string;
    SiteName: string;
    SiteCountryName: string;
    SiteNumber: string;
    Technician: string;
    SubjectId: string;
    SubjectNumber: string;
    VisitName: string;
    SessionName: string;
    SessionDateTime?: string;
    ReceivedDateTime?: string;
    SessionTypeName: string;
    SessionTypeId: string;
    NumTests?: string;
    Status: string;
    StatusId: string;
    Overreader: string;
    ChallengeStatusId?: string;
    ChallengeIsClosed?: boolean;
    AnalysisSets: AnalysisSetStatusAnalyst[];
}

export interface LeanSessionMetadataTableProps {
    sessionMetadata: LeanSessionMetadata;
    selectedAnalysisSetId: string;
}

const GetAnalysisSetStatus = (selectedAnalysisSetId: string, analysisSets: AnalysisSetStatusAnalyst[] = [], isQC = false): string => {

    const analysisSet = analysisSets.find(a => a.Id === selectedAnalysisSetId);

    if (!analysisSet) {
        return "-";
    }

    return analysisSet.Status.Name
}

const GetAnalysisSetAnalyst = (selectedAnalysisSetId: string, analysisSets: AnalysisSetStatusAnalyst[] = []): string => {

    const analysisSet = analysisSets.find(a => a.Id === selectedAnalysisSetId);

    if (!analysisSet || !analysisSet.Analyst || !analysisSet.Analyst.Username) {
        return "-";
    }

    return analysisSet.Analyst.Username;
}

const SessionMetadataTable = ({ sessionMetadata, selectedAnalysisSetId, isQc: isQC }: SessionMetadataTableProps) => {

    const { currentUserPermissions } = useSecurityContext();
    const ViewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }


    return (
        <div className="panel panel-default">
            <div className="panel-body table-scroll">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Study Name</th>
                            <th>Protocol Number</th>
                            <th>Site Number</th>
                            <th>Site Name</th>
                            <th>Site Location</th>
                            <th>Technician</th>
                            <th>Subject Number</th>
                            <th>Visit Name</th>
                            <th>Session Name</th>
                            <th>Session Start</th>
                            <th>Status</th>
                            <th>Age</th>
                            {ViewOverreaderName() ? <th>Overreader</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.StudyName)}`}>{sessionMetadata.StudyName}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.ProtocolNumber)}`}>{sessionMetadata.ProtocolNumber}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SiteNumber)}`}>{isQC ? "-" : sessionMetadata.SiteNumber}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SiteName)}`}>{sessionMetadata.SiteName}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SiteLocation)}`}>{sessionMetadata.SiteCountryName ?? "-"}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.Technician)}`}>{sessionMetadata.Technician ?? "-"}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SubjectNumber)}`}>{isQC ? "-" : sessionMetadata.SubjectNumber}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.VisitName)}`}>{isQC ? "-" : sessionMetadata.VisitName}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SessionName)}`}>{isQC ? "-" : sessionMetadata.SessionName}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.StartedAt)}`}>{formatDateTime(sessionMetadata.SessionDateTime)}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.Status)}`}>{GetAnalysisSetStatus(selectedAnalysisSetId, sessionMetadata.AnalysisSets)}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.Age)}`}>{sessionMetadata.Age != null ? sessionMetadata.Age : '-'}</td>
                            {ViewOverreaderName() ? <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.Overreader)}`}>{GetAnalysisSetAnalyst(selectedAnalysisSetId, sessionMetadata.AnalysisSets)}</td> : null}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}


export const LeanSessionMetadataTable = ({ sessionMetadata, selectedAnalysisSetId }: LeanSessionMetadataTableProps) => {

    const { currentUserPermissions } = useSecurityContext();
    const ViewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    return (
        <div className="panel panel-default">
            <div className="panel-body">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Site Number</th>
                            <th>Site Name</th>
                            <th>Site Location</th>
                            <th>Technician</th>
                            <th>Subject Number</th>
                            <th>Visit Name</th>
                            <th>Session Name</th>
                            <th>Session Start</th>
                            {ViewOverreaderName() ? <th>Overreader</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SiteNumber)}`}>{sessionMetadata.SiteNumber}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SiteName)}`}>{sessionMetadata.SiteName}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SiteLocation)}`}>{sessionMetadata.SiteCountryName}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.Technician)}`}>{sessionMetadata.Technician != null ? sessionMetadata.Technician : "-"}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SubjectNumber)}`}>{sessionMetadata.SubjectNumber}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.VisitName)}`}>{sessionMetadata.VisitName}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.SessionName)}`}>{sessionMetadata.SessionName}</td>
                            <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.StartedAt)}`}>{formatDateTime(sessionMetadata.SessionDateTime)}</td>
                            {ViewOverreaderName() ? <td data-automationid={`${getSessionMetadataTableAutomationId(SessionMetadataTableAutomationId.Overreader)}`}>{GetAnalysisSetAnalyst(selectedAnalysisSetId, sessionMetadata.AnalysisSets)}</td> : null}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default SessionMetadataTable;
